import * as THREE from 'three/webgpu';

export interface SelectionResult {
    type: 'drone' | 'ground' | 'pickup' | 'none';
    id?: string;
    worldPos: { x: number; y: number; z: number };
}

export class TargetSelector {
    private camera: THREE.PerspectiveCamera;
    private droneGroup: THREE.Group | null;
    private scene: THREE.Scene;
    private domElement: HTMLElement;
    private raycaster = new THREE.Raycaster();
    private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    private intersectPoint = new THREE.Vector3();
    private ndcCoord = new THREE.Vector2();

    constructor(
        camera: THREE.PerspectiveCamera,
        droneGroup: THREE.Group | null,
        scene: THREE.Scene,
        domElement: HTMLElement,
    ) {
        this.camera = camera;
        this.droneGroup = droneGroup;
        this.scene = scene;
        this.domElement = domElement;
    }

    setDroneGroup(g: THREE.Group): void {
        this.droneGroup = g;
    }

    select(screenX: number, screenY: number): SelectionResult {
        // getBoundingClientRect handles letter-boxing, CSS scaling, and
        // sidebars — picking lines up with the visible cursor on the canvas.
        const rect = this.domElement.getBoundingClientRect();
        this.ndcCoord.set(
            ((screenX - rect.left) / rect.width) * 2 - 1,
            -((screenY - rect.top) / rect.height) * 2 + 1,
        );
        this.raycaster.setFromCamera(this.ndcCoord, this.camera);

        if (this.droneGroup) {
            const hits = this.raycaster.intersectObjects(this.droneGroup.children, true);
            for (const hit of hits) {
                const object = hit.object as THREE.Object3D & {
                    userData?: { droneId?: string; instanceDroneIds?: string[] };
                };

                if (hit.instanceId !== undefined) {
                    const droneId = object.userData?.instanceDroneIds?.[hit.instanceId];
                    if (droneId) {
                        const p = hit.point;
                        return {
                            type: 'drone',
                            id: droneId,
                            worldPos: { x: p.x, y: p.y, z: p.z },
                        };
                    }
                }

                let obj: THREE.Object3D | null = object;
                while (obj && !obj.userData?.droneId) obj = obj.parent;
                if (obj?.userData?.droneId) {
                    const p = hit.point;
                    return {
                        type: 'drone',
                        id: obj.userData.droneId,
                        worldPos: { x: p.x, y: p.y, z: p.z },
                    };
                }
            }
        }

        if (this.raycaster.ray.intersectPlane(this.groundPlane, this.intersectPoint)) {
            const pt = this.intersectPoint;
            return { type: 'ground', worldPos: { x: pt.x, y: pt.y, z: pt.z } };
        }

        return { type: 'none', worldPos: { x: 0, y: 0, z: 0 } };
    }
}
